// TODO: TS: check ts

import type { JsonString } from "types/all";

const arrayToUpperCase = (array: any[] | undefined): any[] | undefined => {
  return array?.map((element) => (typeof element === "string" ? element.toUpperCase() : element));
};

const elementToUpperCase = (element: any): any => {
  return typeof element === "string" ? element.toUpperCase() : element;
};

const isValueInFilterValues = (value: any, filterValues: any[] | undefined): boolean => {
  return Boolean(arrayToUpperCase(filterValues)?.includes(elementToUpperCase(value))) || filterValues?.length === 0;
};

export const doArraysIntersect = (array1: any[] | undefined, array2: any[] | undefined): boolean => {
  return array2?.length === 0 || (arrayIntersection(array1, array2)?.length ?? 0) > 0;
};

const arrayIntersection = (array1: any[] | undefined, array2: any[] | undefined): any[] => {
  if (!array1 || !array2) return [];
  const array2Uppercase = arrayToUpperCase(array2);
  return array1?.filter((element) => array2Uppercase?.includes(elementToUpperCase(element)));
};

export const filterDataBySinlgeValueFilter = (
  data: any[] | undefined,
  filterValues: any[],
  lookUpField: string,
  nestedLookUpField: null | string = null,
  nullAlternative?: any
): any[] => {
  if (!data) return [];
  return data?.filter((row) => {
    if (!nestedLookUpField) {
      const dataValue = row[lookUpField] ? row[lookUpField] : nullAlternative;
      return isValueInFilterValues(dataValue, filterValues);
    } else {
      const nestedArray: any[] = row[lookUpField]?.map((nestedRow: any) => nestedRow[nestedLookUpField]);
      if (nestedArray && nestedArray?.length === 0) nestedArray.push(nullAlternative);
      return doArraysIntersect(nestedArray, filterValues);
    }
  });
};

export const filterDataBySearchTerm = (data: any[] | undefined, searchTerm: string, lookUpField: string): any[] => {
  if (!data) return [];
  if (searchTerm === "") return data;
  return data?.filter((row) => elementToUpperCase(row[lookUpField]).includes(elementToUpperCase(searchTerm)));
};

const lowestUpperBound = (number: number, array: number[] | undefined): number | string => {
  const allUpper = array?.filter((x) => x > number) || [];
  const upperBound = Math.min(...allUpper);
  return upperBound ? upperBound : "inf";
};

export const filterDataByYear = (
  data: any[] | undefined,
  filterValues: (null | number)[],
  yearBreakPoints: number[],
  lookUpField: string
): any[] => {
  if (!data) return [];
  return data?.filter((row) => {
    const dataYear = row[lookUpField];
    if (filterValues?.length === 0) return true;
    if (!dataYear) return filterValues?.includes(null);
    return lowestUpperBound(dataYear, filterValues as number[]) === lowestUpperBound(dataYear, yearBreakPoints);
  });
};

export const sumByKeys = ({
  data,
  ignoreNullOrUndefined = true,
  keys,
}: {
  data?: any[];
  ignoreNullOrUndefined?: boolean;
  keys?: string[];
}): Record<string, number> | undefined => {
  if (!data) return;
  const sumByKey: Record<string, number> = {};

  data.forEach((obj) => {
    Object.keys(obj).forEach((key) => {
      if (keys?.includes(key)) {
        if (isNumeric(obj[key]) || !ignoreNullOrUndefined) sumByKey[key] = (sumByKey[key] ?? 0) + obj[key];
      }
    });
  });
  return sumByKey;
};

export const minMaxByKey = ({ data, key }: { data?: any[]; key: string }): undefined | { max: any; min: any } => {
  if (!data) return;

  try {
    return data.reduce(
      (total: { max: any; min: any }, current: any) => {
        if (!total.min || current[key] < total.min) total = { ...total, min: current[key] };
        if (!total.max || current[key] > total.max) total = { ...total, max: current[key] };
        return total;
      },
      { max: data[0][key], min: data[0][key] }
    );
  } catch {
    return { max: null, min: null };
  }
};

export const sum = (array: number[] | undefined): number | undefined => {
  if (!array || !(array?.length > 0)) return;
  if (array?.length === 1) return array[0];
  return array[0] + (sum(array.slice(1)) ?? 0);
};

export const average = (array: number[] | undefined): number | undefined => {
  if (!array || !(array?.length > 0)) return;
  return (sum(array) ?? 0) / array.length;
};

export const averageND = (arrayND: number[][] | undefined): number[] => {
  if (!arrayND || !(arrayND?.length > 0)) return [];
  if (!arrayND[0] || !(arrayND[0]?.length > 0)) return [];
  return Array.from(Array(arrayND[0].length)).map((_, index) => average(arrayND.map((row) => row[index])) ?? 0);
};

export const max = (
  array: (number | undefined)[] | undefined,
  replaceUndefinedWith: number = 0
): number | undefined => {
  if (!array || !(array?.length > 0)) return;
  return Math.max(...array.map((value) => value ?? replaceUndefinedWith));
};

export const distinctFilter = <T>(value: T, index: number, self: T[]): boolean => {
  return self.indexOf(value) === index;
};

export const getUniqueValuesFromJson = (data: any[] | undefined, key: string): any[] | undefined => {
  return data?.map((row) => row[key])?.filter(distinctFilter);
};

export const findDataRange = ({
  data,
  dataKeys,
  defaultMax,
  defaultMin,
}: {
  data?: any[];
  dataKeys: string[];
  defaultMax?: any;
  defaultMin?: any;
}): { max: any; min: any } => {
  const { max, min } =
    data?.reduce(
      (total: { max: any; min: any }, current: any) => {
        for (const dataKey of dataKeys) {
          if (total.min === null || (current[dataKey] !== null && current[dataKey] < total.min))
            total.min = current[dataKey];
          if (total.max === null || (current[dataKey] !== null && current[dataKey] > total.max))
            total.max = current[dataKey];
        }
        return total;
      },
      { max: null, min: null }
    ) ?? {};
  return { max: max ?? defaultMax, min: min ?? defaultMin };
};

export const updateTableCell = (
  tableData: any[] | undefined,
  rowId: any,
  dataKey: string,
  newValue: any,
  idColumnName: string
): any[] | undefined => {
  return tableData?.map((row) => {
    if (row[idColumnName] === rowId) return { ...row, [dataKey]: newValue };
    return row;
  });
};

export const filterDataToDateRange = ({
  data,
  dateKey,
  endDate,
  startDate,
}: {
  data?: any[];
  dateKey: string;
  endDate?: Date;
  startDate?: Date;
}): any[] | undefined => {
  if (!data || data?.length === 0 || !startDate || !endDate || !dateKey) return data;
  return data?.filter((row) => {
    const date = new Date(row[dateKey]);
    return date <= endDate && date >= startDate;
  });
};

export const convertDailyToMonthly = ({ data, dateKey }: { data?: any[]; dateKey: string }): any[] | undefined => {
  if (!data || data?.length === 0 || !dateKey) return data;

  const monthlyData: Record<string, any> = {};

  data.forEach((item) => {
    const date = new Date(item[dateKey]);
    const yearMonth = date.toLocaleString("en-US", {
      month: "short",
      year: "numeric",
    });

    if (!monthlyData[yearMonth]) {
      monthlyData[yearMonth] = { [dateKey]: yearMonth };
    }

    Object.entries(item).forEach(([field, value]) => {
      if (field !== dateKey) {
        monthlyData[yearMonth][field] = (monthlyData[yearMonth][field] || 0) + (value as number);
      }
    });
  });
  return Object.values(monthlyData);
};

export const getArraysCrossPointIndex = (arr1: number[], arr2: number[]): number | undefined => {
  const diff = arr1?.map((_, index) => arr1[index] - arr2[index]);
  return [...Array(diff?.length ?? 0).keys()].find(
    (_, index) => (diff[index] >= 0 && diff[index + 1] <= 0) || (diff[index] <= 0 && diff[index + 1] >= 0)
  );
};

export const getSegmentsCrossPointCoords = (
  segA: [[number, number], [number, number]],
  segB: [[number, number], [number, number]]
): [number, number] => {
  const [[xA1, yA1], [xA2, yA2]] = segA;
  const [[xB1, yB1], [xB2, yB2]] = segB;
  const mA = (yA2 - yA1) / (xA2 - xA1);
  const mB = (yB2 - yB1) / (xB2 - xB1);
  const x = (-mB * xB1 + yB1 - yA1 + mA * xA1) / (mA - mB);
  const y = mA * (x - xA1) + yA1;
  return [x, y];
};

export const deepEqual = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (a instanceof Date && b instanceof Date && a.getTime() !== b.getTime()) return false;
  if (Object(a) !== a && Object(b) !== b && a !== b) return false;
  if (typeof a !== typeof b) return false;
  if (!(a instanceof Object) || !(b instanceof Object)) return false;
  if (Array.isArray(a) && Array.isArray(b) && a.length !== b.length) return false;

  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys?.length !== bKeys?.length) return false;

  for (const key of aKeys) {
    if (!bKeys?.includes(key)) return false;
    if (!deepEqual(a[key], b[key])) return false;
  }

  return true;
};

export const isNumeric = (n: any) => {
  return !isNaN(parseFloat(n)) && isFinite(n);
};

export const isNumericGuard = (n: any): n is number => {
  return isNumeric(n);
};

export const deleteKeysFromObjects = (array: any[] | undefined, keys: string[] | undefined): any[] | undefined => {
  return array?.map((obj) => {
    const newObj = { ...(obj ?? {}) };
    keys?.forEach((key) => {
      delete newObj[key];
    });
    return newObj;
  });
};

export const keepKeysFromObjects = (array: any[] | undefined, keys: string[]): any[] | undefined => {
  return array?.map((obj) => {
    const newObj: Record<string, any> = {};
    keys.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        newObj[key] = obj[key];
      }
    });
    return newObj;
  });
};

export const areNumbersClose = (num1: number, num2: number, epsilon = 0.00001): boolean => {
  if (!isNumeric(num1) || !isNumeric(num2)) return false;
  return Math.abs(num1 - num2) < epsilon;
};

export const areCoordsClose = (
  coords1: null | undefined | { lat: number; lng: number },
  coords2: null | undefined | { lat: number; lng: number },
  epsilon = 0.00001
): boolean => {
  if (!coords1 || !coords2) return false;
  return areNumbersClose(coords1.lat, coords2.lat, epsilon) && areNumbersClose(coords1.lng, coords2.lng, epsilon);
};

export const parseJson = <T>(value: JsonString<T>): T | undefined => {
  try {
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
};

export const uniqueNonEmpty = (strings: string[]) => {
  const seen = new Set<string>();
  return strings.filter((string) => {
    const normalized = string.trim();
    if (!normalized || seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });
};

export const parseJsonArray = (raw: null | string | undefined): unknown[] => {
  if (!raw) return [];
  const parsed: unknown = parseJson<string>(raw as JsonString<string>);
  return Array.isArray(parsed) ? parsed : [];
};
